'use client';

import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Clock, Users, AlertTriangle, CheckCircle2, RefreshCw, Download, Filter, Search, ArrowUpRight, ArrowDownRight, Shield, Star,  } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,  } from 'recharts';
import Icon from '@/components/ui/AppIcon';


// ── Types ─────────────────────────────────────────────────────────────────────

interface CallQueueItem {
  id: string;
  caller: string;
  company: string;
  number: string;
  waitTime: number; // seconds
  priority: 'critical' | 'high' | 'normal' | 'low';
  type: 'inbound' | 'callback' | 'transfer';
  issue: string;
  queue: string;
}

interface Agent {
  id: string;
  name: string;
  initials: string;
  status: 'available' | 'on-call' | 'wrap-up' | 'break' | 'offline';
  extension: string;
  callsToday: number;
  avgHandle: string;
  avgWait: string;
  satisfaction: number;
  talkTime: string;
  queue: string;
  currentCaller?: string;
  callDuration?: string;
}

interface CallRecord {
  id: string;
  caller: string;
  company: string;
  number: string;
  type: 'inbound' | 'outbound' | 'missed' | 'transferred';
  duration: string;
  agent: string;
  queue: string;
  time: string;
  outcome: 'resolved' | 'escalated' | 'voicemail' | 'abandoned';
  satisfaction?: number;
}

// ── Mock Data ─────────────────────────────────────────────────────────────────

const callQueue: CallQueueItem[] = [
  { id: 'q1', caller: 'James Okafor', company: 'Meridian BPO', number: '+1 (555) 204-8821', waitTime: 42, priority: 'critical', type: 'inbound', issue: 'VOIP lines down — full outage', queue: 'Technical Support' },
  { id: 'q2', caller: 'Priya Sharma', company: 'CloudBridge Inc.', number: '+44 20 7946 0321', waitTime: 115, priority: 'high', type: 'callback', issue: 'Invoice dispute — overcharge query', queue: 'Billing' },
  { id: 'q3', caller: 'Tom Hargreaves', company: 'Vantage Media', number: '+1 (555) 388-1102', waitTime: 203, priority: 'normal', type: 'inbound', issue: 'Campaign performance review', queue: 'General' },
  { id: 'q4', caller: 'Aisha Noor', company: 'Pinnacle Logistics', number: '+971 4 234 5678', waitTime: 267, priority: 'normal', type: 'inbound', issue: 'Onboarding assistance request', queue: 'General' },
  { id: 'q5', caller: 'Carlos Mendez', company: 'TechVault LLC', number: '+1 (555) 901-4432', waitTime: 312, priority: 'high', type: 'transfer', issue: 'SLA breach escalation — P1 ticket', queue: 'Technical Support' },
  { id: 'q6', caller: 'Fatima Al-Rashid', company: 'Gulf Dynamics', number: '+966 11 234 5678', waitTime: 89, priority: 'low', type: 'callback', issue: 'Contract renewal discussion', queue: 'Sales' },
];

const agents: Agent[] = [
  { id: 'a1', name: 'Layla Khalid', initials: 'LK', status: 'on-call', extension: '2201', callsToday: 34, avgHandle: '4m 12s', avgWait: '0m 38s', satisfaction: 96, talkTime: '2h 24m', queue: 'Technical Support', currentCaller: 'Ravi Patel', callDuration: '3:42' },
  { id: 'a2', name: 'Omar Saeed', initials: 'OS', status: 'available', extension: '2202', callsToday: 28, avgHandle: '3m 45s', avgWait: '0m 22s', satisfaction: 94, talkTime: '1h 45m', queue: 'Billing' },
  { id: 'a3', name: 'Nadia Hassan', initials: 'NH', status: 'on-call', extension: '2203', callsToday: 41, avgHandle: '5m 02s', avgWait: '0m 51s', satisfaction: 91, talkTime: '3h 08m', queue: 'Technical Support', currentCaller: 'Lena Müller', callDuration: '7:15' },
  { id: 'a4', name: 'Khalid Mansoor', initials: 'KM', status: 'wrap-up', extension: '2204', callsToday: 22, avgHandle: '4m 30s', avgWait: '0m 44s', satisfaction: 89, talkTime: '1h 39m', queue: 'General' },
  { id: 'a5', name: 'Sara Al-Farsi', initials: 'SF', status: 'available', extension: '2205', callsToday: 37, avgHandle: '3m 58s', avgWait: '0m 29s', satisfaction: 97, talkTime: '2h 27m', queue: 'Billing' },
  { id: 'a6', name: 'Rami Yousef', initials: 'RY', status: 'break', extension: '2206', callsToday: 19, avgHandle: '4m 11s', avgWait: '0m 35s', satisfaction: 93, talkTime: '1h 19m', queue: 'Sales' },
  { id: 'a7', name: 'Dana Haddad', initials: 'DH', status: 'on-call', extension: '2207', callsToday: 31, avgHandle: '4m 44s', avgWait: '0m 41s', satisfaction: 95, talkTime: '2h 26m', queue: 'General', currentCaller: 'Yuki Tanaka', callDuration: '1:58' },
  { id: 'a8', name: 'Faris Qasim', initials: 'FQ', status: 'offline', extension: '2208', callsToday: 0, avgHandle: '—', avgWait: '—', satisfaction: 92, talkTime: '0m', queue: 'Technical Support' },
];

const callHistory: CallRecord[] = [
  { id: 'h1', caller: 'Ravi Patel', company: 'Nexus Corp', number: '+1 (555) 112-3344', type: 'inbound', duration: '6m 22s', agent: 'Layla Khalid', queue: 'Technical Support', time: '16:48', outcome: 'resolved', satisfaction: 5 },
  { id: 'h2', caller: 'Lena Müller', company: 'EuroTech GmbH', number: '+49 30 1234 5678', type: 'inbound', duration: '3m 10s', agent: 'Nadia Hassan', queue: 'Technical Support', time: '16:41', outcome: 'escalated', satisfaction: 3 },
  { id: 'h3', caller: 'Unknown', company: '—', number: '+1 (555) 900-0011', type: 'missed', duration: '—', agent: '—', queue: 'General', time: '16:35', outcome: 'abandoned' },
  { id: 'h4', caller: 'Yuki Tanaka', company: 'Sakura Digital', number: '+81 3 1234 5678', type: 'outbound', duration: '8m 44s', agent: 'Dana Haddad', queue: 'Sales', time: '16:29', outcome: 'resolved', satisfaction: 5 },
  { id: 'h5', caller: 'Marcus Webb', company: 'Ironclad Finance', number: '+1 (555) 774-2200', type: 'inbound', duration: '2m 05s', agent: 'Omar Saeed', queue: 'Billing', time: '16:22', outcome: 'voicemail' },
  { id: 'h6', caller: 'Amara Diallo', company: 'Sahel Ventures', number: '+221 33 123 4567', type: 'callback', duration: '11m 18s', agent: 'Sara Al-Farsi', queue: 'Billing', time: '16:14', outcome: 'resolved', satisfaction: 4 },
  { id: 'h7', caller: 'Ivan Petrov', company: 'Baltic Systems', number: '+370 5 123 4567', type: 'transferred', duration: '4m 55s', agent: 'Khalid Mansoor', queue: 'Technical Support', time: '16:07', outcome: 'resolved', satisfaction: 4 },
  { id: 'h8', caller: 'Sophie Laurent', company: 'Lumière Media', number: '+33 1 23 45 67 89', type: 'inbound', duration: '5m 33s', agent: 'Rami Yousef', queue: 'Sales', time: '15:58', outcome: 'escalated', satisfaction: 2 },
];

const hourlyVolume = [
  { hour: '08:00', inbound: 12, outbound: 4, missed: 1 },
  { hour: '09:00', inbound: 28, outbound: 9, missed: 2 },
  { hour: '10:00', inbound: 41, outbound: 14, missed: 3 },
  { hour: '11:00', inbound: 35, outbound: 11, missed: 2 },
  { hour: '12:00', inbound: 18, outbound: 6, missed: 4 },
  { hour: '13:00', inbound: 22, outbound: 8, missed: 2 },
  { hour: '14:00', inbound: 38, outbound: 13, missed: 1 },
  { hour: '15:00', inbound: 44, outbound: 16, missed: 3 },
  { hour: '16:00', inbound: 31, outbound: 10, missed: 2 },
  { hour: '17:00', inbound: 19, outbound: 7, missed: 1 },
];

const slaData = [
  { name: 'Mon', met: 94, breached: 6 },
  { name: 'Tue', met: 91, breached: 9 },
  { name: 'Wed', met: 96, breached: 4 },
  { name: 'Thu', met: 88, breached: 12 },
  { name: 'Fri', met: 93, breached: 7 },
  { name: 'Sat', met: 97, breached: 3 },
  { name: 'Sun', met: 95, breached: 5 },
];

const avgHandleTimeTrend = [
  { time: '08:00', ht: 3.8 }, { time: '09:00', ht: 4.1 }, { time: '10:00', ht: 4.4 },
  { time: '11:00', ht: 4.2 }, { time: '12:00', ht: 5.1 }, { time: '13:00', ht: 4.7 },
  { time: '14:00', ht: 4.3 }, { time: '15:00', ht: 4.0 }, { time: '16:00', ht: 3.9 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatWait(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const statusConfig = {
  'available': { label: 'Available', textColor: 'text-success', dot: 'bg-success', bg: 'bg-success/10' },
  'on-call':   { label: 'On Call',   textColor: 'text-primary', dot: 'bg-primary', bg: 'bg-primary/10' },
  'wrap-up':   { label: 'Wrap-Up',   textColor: 'text-cyan-400', dot: 'bg-cyan-400', bg: 'bg-cyan-400/10' },
  'break':     { label: 'Break',     textColor: 'text-warning', dot: 'bg-warning', bg: 'bg-warning/10' },
  'offline':   { label: 'Offline',   textColor: 'text-muted-foreground', dot: 'bg-muted-foreground', bg: 'bg-muted/50' },
};

const priorityConfig = {
  critical: { label: 'Critical', textColor: 'text-danger',  bg: 'bg-danger/10',  border: 'border-danger/30' },
  high:     { label: 'High',     textColor: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/30' },
  normal:   { label: 'Normal',   textColor: 'text-success', bg: 'bg-success/10', border: 'border-success/30' },
  low:      { label: 'Low',      textColor: 'text-muted-foreground', bg: 'bg-muted/50', border: 'border-border' },
};

const typeConfig = {
  inbound:    { label: 'Inbound',    icon: PhoneIncoming,  color: 'text-success' },
  outbound:   { label: 'Outbound',   icon: PhoneOutgoing,  color: 'text-primary' },
  callback:   { label: 'Callback',   icon: Phone,          color: 'text-cyan-400' },
  transfer:   { label: 'Transfer',   icon: Phone,          color: 'text-violet-400' },
  missed:     { label: 'Missed',     icon: PhoneMissed,    color: 'text-danger' },
  transferred:{ label: 'Transferred',icon: Phone,          color: 'text-violet-400' },
};

const outcomeConfig = {
  resolved:   { label: 'Resolved',   color: 'text-success', bg: 'bg-success/10' },
  escalated:  { label: 'Escalated',  color: 'text-warning', bg: 'bg-warning/10' },
  voicemail:  { label: 'Voicemail',  color: 'text-muted-foreground', bg: 'bg-muted/50' },
  abandoned:  { label: 'Abandoned',  color: 'text-danger',  bg: 'bg-danger/10' },
};

// ── KPI Cards ─────────────────────────────────────────────────────────────────

const kpis = [
  { label: 'Calls Today', value: '212', change: '+18', up: true, icon: Phone, color: 'text-primary', bg: 'bg-primary/10' },
  { label: 'In Queue Now', value: '6', change: '+2', up: false, icon: PhoneIncoming, color: 'text-warning', bg: 'bg-warning/10' },
  { label: 'Agents Online', value: '7', change: '1 offline', up: true, icon: Users, color: 'text-success', bg: 'bg-success/10' },
  { label: 'Avg Wait Time', value: '1m 48s', change: '-12s', up: true, icon: Clock, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
  { label: 'SLA Met Today', value: '93.4%', change: '+1.2%', up: true, icon: Shield, color: 'text-violet-400', bg: 'bg-violet-400/10' },
  { label: 'Avg CSAT', value: '4.6 / 5', change: '+0.1', up: true, icon: Star, color: 'text-amber-400', bg: 'bg-amber-400/10' },
];

// ── Main Component ─────────────────────────────────────────────────────────────

export default function VoipPage() {
  const [activeTab, setActiveTab] = useState<'queue' | 'agents' | 'history' | 'analytics'>('queue');
  const [historyFilter, setHistoryFilter] = useState<string>('all');
  const [agentFilter, setAgentFilter] = useState<string>('all');
  const [historySearch, setHistorySearch] = useState('');
  const [tick, setTick] = useState(0);

  // Simulate live ticker
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const onCallCount   = agents.filter(a => a.status === 'on-call').length;
  const availableCount = agents.filter(a => a.status === 'available').length;
  const filteredAgents = agentFilter === 'all' ? agents : agents.filter(a => a.status === agentFilter);

  const filteredHistory = callHistory.filter(r => {
    const matchType = historyFilter === 'all' || r.type === historyFilter || r.outcome === historyFilter;
    const matchSearch = historySearch === '' ||
      r.caller.toLowerCase().includes(historySearch.toLowerCase()) ||
      r.company.toLowerCase().includes(historySearch.toLowerCase()) ||
      r.agent.toLowerCase().includes(historySearch.toLowerCase());
    return matchType && matchSearch;
  });

  const tabs = [
    { id: 'queue',     label: 'Live Queue',    badge: callQueue.length },
    { id: 'agents',    label: 'Agent Status',  badge: null },
    { id: 'history',   label: 'Call History',  badge: null },
    { id: 'analytics', label: 'Analytics',     badge: null },
  ] as const;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">VOIP / Call Centre</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Live call queue, agent performance, SLA tracking &amp; call history</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="flex items-center gap-1.5 text-xs text-success font-medium bg-success/10 px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
              Live
            </span>
            <button className="flex items-center gap-2 bg-muted text-foreground px-3 py-2 rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors border border-border">
              <RefreshCw size={14} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button className="flex items-center gap-2 bg-primary text-primary-foreground px-3 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
              <Download size={14} />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
          {kpis.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-9 h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                    <Icon size={18} className={kpi.color} />
                  </div>
                  <span className={`text-xs font-medium flex items-center gap-0.5 ${kpi.up ? 'text-success' : 'text-danger'}`}>
                    {kpi.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {kpi.change}
                  </span>
                </div>
                <p className="text-xl font-bold text-foreground font-tabular">{kpi.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{kpi.label}</p>
              </div>
            );
          })}
        </div>

        {/* ── Real-Time Status Board ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'On Call', value: onCallCount, color: 'text-primary', bg: 'bg-primary/10', dot: 'bg-primary' },
            { label: 'Available', value: availableCount, color: 'text-success', bg: 'bg-success/10', dot: 'bg-success' },
            { label: 'Wrap-Up', value: agents.filter(a => a.status === 'wrap-up').length, color: 'text-cyan-400', bg: 'bg-cyan-400/10', dot: 'bg-cyan-400' },
            { label: 'On Break', value: agents.filter(a => a.status === 'break').length, color: 'text-warning', bg: 'bg-warning/10', dot: 'bg-warning' },
          ].map(s => (
            <div key={s.label} className={`${s.bg} border border-border rounded-xl p-4 flex items-center gap-3`}>
              <span className={`w-3 h-3 rounded-full ${s.dot} animate-pulse flex-shrink-0`} />
              <div>
                <p className={`text-2xl font-bold ${s.color} font-tabular`}>{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Tabs ── */}
        <div className="flex gap-1 bg-card border border-border rounded-xl p-1 mb-6 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {tab.label}
              {tab.badge !== null && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
                }`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════════════
            TAB: LIVE QUEUE
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'queue' && (
          <div className="space-y-3">
            {/* SLA Warning Banner */}
            <div className="flex items-center gap-3 bg-danger/10 border border-danger/30 rounded-xl px-4 py-3">
              <AlertTriangle size={16} className="text-danger flex-shrink-0" />
              <p className="text-sm text-danger font-medium">
                2 callers approaching SLA breach threshold — longest wait: <span className="font-bold">5m 12s</span>
              </p>
            </div>

            {/* Queue Table — desktop */}
            <div className="bg-card border border-border rounded-xl overflow-hidden hidden md:block">
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">Live Call Queue</h2>
                <span className="text-xs text-muted-foreground">{callQueue.length} waiting</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {['Priority', 'Caller', 'Company', 'Number', 'Wait', 'Type', 'Issue', 'Queue', 'Action'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {callQueue.map((item, idx) => {
                      const p = priorityConfig[item.priority];
                      const t = typeConfig[item.type];
                      const TypeIcon = t.icon;
                      return (
                        <tr key={item.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${idx === 0 ? 'bg-danger/5' : ''}`}>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${p.bg} ${p.textColor} border ${p.border}`}>
                              {item.priority === 'critical' && <AlertTriangle size={10} />}
                              {p.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-2xs font-semibold text-primary">{item.caller.split(' ').map(n => n[0]).join('')}</span>
                              </div>
                              <span className="font-medium text-foreground whitespace-nowrap">{item.caller}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{item.company}</td>
                          <td className="px-4 py-3 text-muted-foreground font-tabular text-xs whitespace-nowrap">{item.number}</td>
                          <td className="px-4 py-3">
                            <span className={`font-tabular font-semibold text-sm ${item.waitTime > 240 ? 'text-danger' : item.waitTime > 120 ? 'text-warning' : 'text-success'}`}>
                              {formatWait(item.waitTime + tick % 60)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`flex items-center gap-1 text-xs font-medium ${t.color}`}>
                              <TypeIcon size={12} />
                              {t.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate">{item.issue}</td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full whitespace-nowrap">{item.queue}</span>
                          </td>
                          <td className="px-4 py-3">
                            <button className="text-xs bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap">
                              Assign
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Queue Cards — mobile */}
            <div className="md:hidden space-y-3">
              {callQueue.map((item) => {
                const p = priorityConfig[item.priority];
                const t = typeConfig[item.type];
                const TypeIcon = t.icon;
                return (
                  <div key={item.id} className="bg-card border border-border rounded-xl p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-foreground">{item.caller}</p>
                        <p className="text-xs text-muted-foreground">{item.company}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${p.bg} ${p.textColor} border ${p.border} flex-shrink-0`}>
                        {p.label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{item.issue}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`flex items-center gap-1 text-xs font-medium ${t.color}`}><TypeIcon size={12} />{t.label}</span>
                        <span className={`font-tabular font-semibold text-sm ${item.waitTime > 240 ? 'text-danger' : item.waitTime > 120 ? 'text-warning' : 'text-success'}`}>
                          {formatWait(item.waitTime)}
                        </span>
                      </div>
                      <button className="text-xs bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1.5 rounded-lg font-medium transition-colors">Assign</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB: AGENT STATUS
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'agents' && (
          <div className="space-y-4">
            {/* Filter */}
            <div className="flex flex-wrap gap-2">
              {['all', 'available', 'on-call', 'wrap-up', 'break', 'offline'].map(f => (
                <button
                  key={f}
                  onClick={() => setAgentFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                    agentFilter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {f === 'all' ? 'All Agents' : f.replace('-', ' ')}
                </button>
              ))}
            </div>

            {/* Agent Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {filteredAgents.map(agent => {
                const s = statusConfig[agent.status];
                return (
                  <div key={agent.id} className="bg-card border border-border rounded-xl p-4 space-y-4">
                    {/* Agent Header */}
                    <div className="flex items-center gap-3">
                      <div className="relative flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-sm font-bold text-primary">{agent.initials}</span>
                        </div>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card ${s.dot}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-foreground text-sm truncate">{agent.name}</p>
                        <p className="text-xs text-muted-foreground">Ext. {agent.extension} · {agent.queue}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${s.bg} ${s.textColor} flex-shrink-0`}>
                        {s.label}
                      </span>
                    </div>

                    {/* Active Call */}
                    {agent.status === 'on-call' && agent.currentCaller && (
                      <div className="bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 flex items-center gap-2">
                        <Phone size={12} className="text-primary animate-pulse flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-primary truncate">{agent.currentCaller}</p>
                        </div>
                        <span className="text-xs font-tabular text-primary font-semibold flex-shrink-0">{agent.callDuration}</span>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: 'Calls Today', value: agent.callsToday.toString() },
                        { label: 'Avg Handle', value: agent.avgHandle },
                        { label: 'Talk Time', value: agent.talkTime },
                        { label: 'CSAT', value: `${agent.satisfaction}%` },
                      ].map(stat => (
                        <div key={stat.label} className="bg-muted/40 rounded-lg px-2.5 py-2">
                          <p className="text-xs text-muted-foreground">{stat.label}</p>
                          <p className="text-sm font-semibold text-foreground font-tabular">{stat.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* CSAT Bar */}
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Satisfaction</span>
                        <span className={`font-semibold ${agent.satisfaction >= 95 ? 'text-success' : agent.satisfaction >= 85 ? 'text-warning' : 'text-danger'}`}>
                          {agent.satisfaction}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${agent.satisfaction >= 95 ? 'bg-success' : agent.satisfaction >= 85 ? 'bg-warning' : 'bg-danger'}`}
                          style={{ width: `${agent.satisfaction}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB: CALL HISTORY
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search caller, company, agent…"
                  value={historySearch}
                  onChange={e => setHistorySearch(e.target.value)}
                  className="w-full bg-muted border border-border rounded-lg pl-9 pr-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {['all', 'inbound', 'outbound', 'missed', 'resolved', 'escalated'].map(f => (
                  <button
                    key={f}
                    onClick={() => setHistoryFilter(f)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors capitalize ${
                      historyFilter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {f === 'all' ? 'All' : f}
                  </button>
                ))}
              </div>
            </div>

            {/* History Table — desktop */}
            <div className="bg-card border border-border rounded-xl overflow-hidden hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {['Time', 'Caller', 'Company', 'Number', 'Type', 'Duration', 'Agent', 'Queue', 'Outcome', 'CSAT'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.map(record => {
                      const t = typeConfig[record.type];
                      const TypeIcon = t.icon;
                      const o = outcomeConfig[record.outcome];
                      return (
                        <tr key={record.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-tabular text-muted-foreground text-xs whitespace-nowrap">{record.time}</td>
                          <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">{record.caller}</td>
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{record.company}</td>
                          <td className="px-4 py-3 text-muted-foreground font-tabular text-xs whitespace-nowrap">{record.number}</td>
                          <td className="px-4 py-3">
                            <span className={`flex items-center gap-1 text-xs font-medium ${t.color}`}>
                              <TypeIcon size={12} />
                              {t.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-tabular text-muted-foreground text-xs whitespace-nowrap">{record.duration}</td>
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{record.agent || '—'}</td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full whitespace-nowrap">{record.queue}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${o.bg} ${o.color}`}>{o.label}</span>
                          </td>
                          <td className="px-4 py-3">
                            {record.satisfaction ? (
                              <div className="flex items-center gap-1">
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <Star key={i} size={10} className={i < record.satisfaction! ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'} />
                                ))}
                              </div>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* History Cards — mobile */}
            <div className="md:hidden space-y-3">
              {filteredHistory.map(record => {
                const t = typeConfig[record.type];
                const TypeIcon = t.icon;
                const o = outcomeConfig[record.outcome];
                return (
                  <div key={record.id} className="bg-card border border-border rounded-xl p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-foreground">{record.caller}</p>
                        <p className="text-xs text-muted-foreground">{record.company} · {record.time}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${o.bg} ${o.color} flex-shrink-0`}>{o.label}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className={`flex items-center gap-1 font-medium ${t.color}`}><TypeIcon size={11} />{t.label}</span>
                      <span className="text-muted-foreground">{record.duration}</span>
                      <span className="text-muted-foreground">{record.agent || '—'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB: ANALYTICS
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Row 1: Volume + SLA */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Hourly Call Volume */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-foreground">Hourly Call Volume</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Inbound, outbound &amp; missed calls today</p>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={hourlyVolume} barSize={8} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="hour" tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="inbound" name="Inbound" fill="var(--primary)" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="outbound" name="Outbound" fill="var(--success)" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="missed" name="Missed" fill="var(--danger)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* SLA Compliance */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-foreground">SLA Compliance — 7 Days</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">% of calls answered within SLA target</p>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={slaData} barSize={20}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
                    <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} formatter={(v: number) => [`${v}%`]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="met" name="SLA Met" fill="var(--success)" radius={[4, 4, 0, 0]} stackId="a" />
                    <Bar dataKey="breached" name="Breached" fill="var(--danger)" radius={[4, 4, 0, 0]} stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Row 2: Avg Handle Time + SLA Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Avg Handle Time Trend */}
              <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-foreground">Avg Handle Time Trend</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Minutes per call throughout the day</p>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={avgHandleTimeTrend}>
                    <defs>
                      <linearGradient id="htGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="time" tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis domain={[3, 6]} tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} unit="m" />
                    <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} formatter={(v: number) => [`${v}m`]} />
                    <Area type="monotone" dataKey="ht" name="Avg Handle" stroke="var(--accent)" strokeWidth={2} fill="url(#htGrad)" dot={{ fill: 'var(--accent)', r: 3 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* SLA KPI Panel */}
              <div className="bg-card border border-border rounded-xl p-5 flex flex-col gap-4">
                <h3 className="text-sm font-semibold text-foreground">SLA Targets</h3>
                {[
                  { label: 'Answer in 20s', target: 80, actual: 87, met: true },
                  { label: 'FCR Rate', target: 75, actual: 82, met: true },
                  { label: 'Abandon Rate', target: 5, actual: 3.2, met: true, invert: true },
                  { label: 'Avg Speed of Answer', target: 30, actual: 24, met: true, unit: 's' },
                  { label: 'CSAT ≥ 4.0', target: 90, actual: 92, met: true },
                ].map(sla => (
                  <div key={sla.label}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">{sla.label}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-foreground font-tabular">
                          {sla.actual}{sla.unit || '%'}
                        </span>
                        <CheckCircle2 size={12} className="text-success" />
                      </div>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-success transition-all"
                        style={{ width: `${Math.min((sla.actual / (sla.invert ? sla.target * 2 : 100)) * 100, 100)}%` }}
                      />
                    </div>
                    <p className="text-2xs text-muted-foreground mt-0.5">Target: {sla.target}{sla.unit || '%'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 3: Agent Leaderboard */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">Agent Performance Leaderboard</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Ranked by calls handled today</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {['Rank', 'Agent', 'Queue', 'Calls', 'Avg Handle', 'Talk Time', 'CSAT', 'Status'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...agents].filter(a => a.callsToday > 0).sort((a, b) => b.callsToday - a.callsToday).map((agent, idx) => {
                      const s = statusConfig[agent.status];
                      return (
                        <tr key={agent.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <span className={`text-sm font-bold font-tabular ${idx === 0 ? 'text-amber-400' : idx === 1 ? 'text-slate-400' : idx === 2 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                              #{idx + 1}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-2xs font-bold text-primary">{agent.initials}</span>
                              </div>
                              <span className="font-medium text-foreground whitespace-nowrap">{agent.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{agent.queue}</td>
                          <td className="px-4 py-3 font-tabular font-semibold text-foreground">{agent.callsToday}</td>
                          <td className="px-4 py-3 font-tabular text-muted-foreground text-xs whitespace-nowrap">{agent.avgHandle}</td>
                          <td className="px-4 py-3 font-tabular text-muted-foreground text-xs whitespace-nowrap">{agent.talkTime}</td>
                          <td className="px-4 py-3">
                            <span className={`text-sm font-semibold font-tabular ${agent.satisfaction >= 95 ? 'text-success' : agent.satisfaction >= 85 ? 'text-warning' : 'text-danger'}`}>
                              {agent.satisfaction}%
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`flex items-center gap-1.5 text-xs font-medium ${s.textColor}`}>
                              <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                              {s.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
}
